'use client';

import React, { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Package,
  Clock,
  CheckCircle,
  XCircle,
  ChevronRight,
  ShoppingBag,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTenant } from '@/contexts/TenantContext';
import {
  createCommerceClient,
  type CustomerOrder,
} from '@/lib/api/clients/commerce';

const statusConfig = {
  CONFIRMED: {
    label: 'Confirmed',
    icon: CheckCircle,
    className: 'text-blue-600 bg-blue-50',
  },
  PROCESSING: {
    label: 'Processing',
    icon: Clock,
    className: 'text-amber-600 bg-amber-50',
  },
  FULFILLED: {
    label: 'Fulfilled',
    icon: Package,
    className: 'text-green-600 bg-green-50',
  },
  COMPLETED: {
    label: 'Completed',
    icon: CheckCircle,
    className: 'text-green-600 bg-green-50',
  },
  CANCELLED: {
    label: 'Cancelled',
    icon: XCircle,
    className: 'text-red-600 bg-red-50',
  },
} as const satisfies Record<string, { label: string; icon: React.ElementType; className: string }>;

const defaultOrderStatus = statusConfig.CONFIRMED;

export const OrderHistory: React.FC = () => {
  const pathname = usePathname();
  const locale = pathname.match(/^\/([a-z]{2})(\/|$)/)?.[1] ?? 'en';
  const { organisationId } = useTenant();

  const [orders, setOrders] = useState<CustomerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'active' | 'completed'>(
    'all',
  );

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const client = createCommerceClient(organisationId);
      const result = await client.getCustomerOrders({ limit: 50 });
      setOrders(result.items);
    } catch {
      setError('Unable to load orders. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const filtered = orders.filter((o) => {
    if (activeTab === 'active')
      return ['CONFIRMED', 'PROCESSING', 'FULFILLED'].includes(o.status);
    if (activeTab === 'completed')
      return ['COMPLETED', 'CANCELLED'].includes(o.status);
    return true;
  });

  const formatPrice = (amount: number, currency: string) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency }).format(
      amount,
    );

  return (
    <div className="animate-[fadeIn_0.3s_ease-out] pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-semibold text-stone-900 tracking-tight">
            Orders
          </h2>
          <p className="text-stone-500 mt-2 text-lg font-light">
            Track and manage your orders.
          </p>
        </div>
        <Link href={`/${locale}/shop/products`}>
          <button className="inline-flex items-center gap-2 rounded-xl bg-stone-900 px-5 py-2.5 text-sm font-semibold text-white shadow-xl shadow-stone-200 hover:bg-stone-800 transition-colors">
            <ShoppingBag size={18} />
            Continue Shopping
          </button>
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-stone-100 p-1 rounded-xl w-fit mb-8">
        {(['all', 'active', 'completed'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-5 py-2 rounded-lg text-sm font-medium transition-colors capitalize',
              activeTab === tab
                ? 'bg-white text-stone-900 shadow-sm'
                : 'text-stone-500 hover:text-stone-900',
            )}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-stone-400" />
        </div>
      )}

      {/* Error state */}
      {!loading && error && (
        <div className="text-center py-16">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <p className="text-stone-600 mb-4">{error}</p>
          <button
            onClick={fetchOrders}
            className="text-sm font-medium text-stone-900 underline underline-offset-2 hover:text-stone-700 transition-colors"
          >
            Try again
          </button>
        </div>
      )}

      {/* Empty state */}
      {!loading && !error && filtered.length === 0 && (
        <div className="text-center py-20">
          <Package className="mx-auto h-16 w-16 text-stone-300 mb-4" />
          <h3 className="text-xl font-semibold text-stone-900 mb-2">
            No orders yet
          </h3>
          <p className="text-stone-500">
            Your order history will appear here once you make a purchase.
          </p>
        </div>
      )}

      {/* Order list */}
      {!loading && !error && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((order) => {
            const config =
              statusConfig[order.status as keyof typeof statusConfig] ?? defaultOrderStatus;
            const StatusIcon = config.icon;

            return (
              <Link
                key={order.id}
                href={`/${locale}/account/orders/${order.id}`}
                className="flex items-center justify-between p-4 rounded-xl border border-stone-200 bg-white hover:border-stone-300 hover:shadow-sm transition-all"
              >
                <div className="flex items-center gap-4">
                  <div className={cn('p-2 rounded-lg', config.className)}>
                    <StatusIcon size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-stone-900">
                      Order #{order.orderNumber}
                    </p>
                    <p className="text-sm text-stone-500">
                      {new Date(order.createdAt).toLocaleDateString(locale, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}{' '}
                      &middot; {order.itemCount} item
                      {order.itemCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-stone-900">
                    {formatPrice(order.total, order.currency)}
                  </span>
                  <ChevronRight size={16} className="text-stone-400" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};
